/**
 * Cambio della propria password dalla pagina Impostazioni.
 *
 * Prima l'unico modo era "Password dimenticata?" dalla pagina di login: bisognava
 * uscire, farsi mandare un'email e seguire il link. Qui lo si fa da dentro, in due
 * passi: si conferma la password attuale, poi si sceglie quella nuova.
 *
 * La password attuale si controlla rifacendo l'accesso con quella. Oltre a
 * escludere chi trovasse il computer acceso e lasciato incustodito, rinfresca la
 * sessione: se su Supabase e' attiva la regola "per cambiare password serve un
 * accesso recente", il salvataggio non viene rifiutato. Un tentativo sbagliato
 * non scollega l'utente: la libreria tocca la sessione solo se l'accesso riesce.
 */

export const LUNGHEZZA_MINIMA = 8;

export type ChiaveRequisito = 'lunghezza' | 'lettereNumeri' | 'diversa' | 'coincidono';

export interface Requisito {
  chiave: ChiaveRequisito;
  testo: string;
  ok: boolean;
}

/** La parte del client Supabase che serve qui: basta questo per provarla senza server. */
export interface AuthPerCambioPassword {
  signInWithPassword(credenziali: { email: string; password: string }): Promise<{ error: unknown }>;
  updateUser(dati: { password: string }): Promise<{ error: unknown }>;
}

/** Messaggio in chiaro, oppure null quando e' andato tutto bene. */
export type EsitoPassword = string | null;

type Fase = 'verifica' | 'salvataggio';

const RAGIONI_DEBOLEZZA: Record<string, string> = {
  length: 'è troppo corta',
  characters: 'mancano alcuni tipi di caratteri richiesti (maiuscole, numeri o simboli)',
  pwned: 'compare negli elenchi di password rubate che girano in rete'
};

export function requisitiNuovaPassword(nuova: string, conferma: string, attuale: string): Requisito[] {
  return [
    {
      chiave: 'lunghezza',
      testo: `Almeno ${LUNGHEZZA_MINIMA} caratteri`,
      ok: nuova.length >= LUNGHEZZA_MINIMA
    },
    {
      chiave: 'lettereNumeri',
      testo: 'Lettere e numeri insieme',
      ok: /\p{L}/u.test(nuova) && /\d/.test(nuova)
    },
    {
      chiave: 'diversa',
      testo: 'Diversa da quella attuale',
      ok: nuova.length > 0 && nuova !== attuale
    },
    {
      chiave: 'coincidono',
      testo: 'Scritta uguale nelle due caselle',
      ok: conferma.length > 0 && nuova === conferma
    }
  ];
}

export function nuovaPasswordValida(requisiti: readonly Requisito[]): boolean {
  return requisiti.every((r) => r.ok);
}

export async function verificaPasswordAttuale(
  auth: AuthPerCambioPassword,
  email: string,
  attuale: string
): Promise<EsitoPassword> {
  if (!email) return 'Non trovo l’email del tuo account. Esci, rientra e riprova.';

  try {
    const { error } = await auth.signInWithPassword({ email, password: attuale });
    return error ? spiegaErrore(error, 'verifica') : null;
  } catch (errore) {
    return spiegaErrore(errore, 'verifica');
  }
}

export async function salvaNuovaPassword(auth: AuthPerCambioPassword, nuova: string): Promise<EsitoPassword> {
  try {
    const { error } = await auth.updateUser({ password: nuova });
    return error ? spiegaErrore(error, 'salvataggio') : null;
  } catch (errore) {
    return spiegaErrore(errore, 'salvataggio');
  }
}

interface ErroreAuth {
  code?: string;
  status?: number;
  reasons?: string[];
}

const leggiErrore = (errore: unknown): ErroreAuth => {
  if (!errore || typeof errore !== 'object') return {};
  const { code, status, reasons } = errore as Record<string, unknown>;
  return {
    code: typeof code === 'string' ? code : undefined,
    status: typeof status === 'number' ? status : undefined,
    reasons: Array.isArray(reasons) ? reasons.filter((r): r is string => typeof r === 'string') : undefined
  };
};

/** Il server non ha risposto affatto: rete assente, o Supabase irraggiungibile. */
const erroreDiRete = (errore: unknown, { status }: ErroreAuth): boolean =>
  errore instanceof TypeError ||
  (typeof errore === 'object' && errore !== null && (errore as { name?: unknown }).name === 'AuthRetryableFetchError') ||
  status === 0;

function spiegaErrore(errore: unknown, fase: Fase): string {
  const dati = leggiErrore(errore);

  if (erroreDiRete(errore, dati)) {
    return 'Non riesco a raggiungere il server. Controlla la connessione e riprova.';
  }
  if (dati.code === 'over_request_rate_limit' || dati.status === 429) {
    return 'Troppi tentativi di fila. Aspetta qualche minuto e riprova.';
  }

  if (fase === 'verifica') {
    return dati.code === 'invalid_credentials'
      ? 'La password attuale non è giusta.'
      : 'Non sono riuscito a controllare la password attuale. Riprova.';
  }

  switch (dati.code) {
    case 'same_password':
      return 'La nuova password è uguale a quella attuale: scegline una diversa.';
    case 'weak_password': {
      const perche = (dati.reasons ?? []).map((r) => RAGIONI_DEBOLEZZA[r]).filter(Boolean);
      return perche.length
        ? `Il server non la accetta: ${perche.join('; ')}. Scegline un’altra.`
        : 'Il server la considera troppo debole. Scegline un’altra.';
    }
    case 'reauthentication_needed':
    case 'session_not_found':
    case 'session_expired':
      return 'Per sicurezza serve rientrare: esci, rientra e ripeti il cambio.';
    default:
      return 'La password non è stata cambiata. Riprova tra poco.';
  }
}
